import { Controller } from "@hotwired/stimulus"

// MSDS 배치 진행 상태 — 탭2 우측.
//
// msds-batch:created (좌측 controller 가 발행) 이벤트 받으면 폴링 시작.
// 2초 주기로 progress URL 호출 → items list 갱신.
// 모든 item 이 terminal 이면 폴링 중단.
export default class extends Controller {
  static targets = ["summary", "tableWrap", "tableBody", "actionBar", "actionLabel", "registerAllBtn"]
  static values = {
    progressUrlTemplate: String,
    registerAllUrlTemplate: String,
    initialBatchId: String
  }

  // 상태 → (라벨, 색 토큰, 아이콘) 매핑
  static STATUS_META = {
    queued:        { label: "대기",       color: "gray-500",    icon: "clock" },
    uploading:     { label: "업로드중",   color: "info-700",    icon: "upload" },
    uploaded:      { label: "업로드 완료", color: "info-700",    icon: "check" },
    extracting:    { label: "추출중",     color: "warning-700", icon: "loader" },
    extracted:     { label: "추출 완료",  color: "primary-500", icon: "file-check" },
    registered:    { label: "등록 완료",  color: "success-700", icon: "check-circle" },
    upload_failed: { label: "업로드 실패", color: "danger-700",  icon: "x-circle" },
    extract_failed:{ label: "추출 실패",  color: "danger-700",  icon: "x-circle" },
    skipped:       { label: "보류",       color: "gray-500",    icon: "minus-circle" }
  }

  connect() {
    this.batchId = null
    this.timer = null
    this._listener = (e) => this.start(e.detail.batchId)
    document.addEventListener("msds-batch:created", this._listener)

    // 페이지 진입 시 서버가 active batch 를 발견했으면 자동 폴링 시작 (새로고침·복귀 재개).
    if (this.hasInitialBatchIdValue && this.initialBatchIdValue) {
      this.start(this.initialBatchIdValue)
    }
  }

  disconnect() {
    document.removeEventListener("msds-batch:created", this._listener)
    this._stopPolling()
  }

  start(batchId) {
    if (!batchId) return
    this.batchId = batchId
    this._poll()
    this._stopPolling()
    this.timer = setInterval(() => this._poll(), 2000)
  }

  // 빈 상태 (batch 없거나 [새 배치 시작] 직후) 의 table body + 액션 바 갱신.
  _renderEmpty() {
    this.summaryTarget.textContent = ""
    this.actionLabelTarget.textContent = "업로드된 파일이 없습니다"
    this.registerAllBtnTarget.disabled = true
    this.registerAllBtnTarget.innerHTML = `<i data-lucide="check-check" style="width: 14px; height: 14px;"></i> 모두 등록`
    this.registerAllBtnTarget.dataset.action = "click->msds-batch-progress#registerAll"
    this.registerAllBtnTarget.classList.remove("bg-[var(--primary-700)]", "hover:bg-[var(--primary-900)]")
    this.registerAllBtnTarget.classList.add("bg-[var(--success-700)]", "hover:bg-[#1B5E20]")
    this.tableBodyTarget.innerHTML = `
      <tr>
        <td colspan="5" class="px-3 py-8 text-center text-sm text-[var(--gray-400)]">
          업로드된 파일이 없습니다.
        </td>
      </tr>
    `
    if (typeof window.lucide !== "undefined") window.lucide.createIcons()
  }

  async _poll() {
    if (!this.batchId) return
    const url = this.progressUrlTemplateValue.replace("BATCH_ID", this.batchId)
    try {
      const res = await fetch(url, { headers: { Accept: "application/json" } })
      const json = await res.json()
      if (!json.success) return
      this._render(json.data)
      // 모든 item 이 terminal 이면 폴링 중단
      const allDone = json.data.pending_count === 0
      if (allDone) this._stopPolling()
    } catch (err) {
      // 일시적 오류 무시 (다음 tick 재시도)
    }
  }

  _stopPolling() {
    if (this.timer) {
      clearInterval(this.timer)
      this.timer = null
    }
  }

  _render(data) {
    // registered 행만 list 에서 제외 (이미 Product 로 등록됨).
    // skipped 는 사용자가 명시적으로 [삭제] 할 때까지 노출 — 결정 이력으로.
    const visibleItems = data.items.filter(i => i.status !== "registered")
    const registeredCount = data.items.filter(i => i.status === "registered").length
    const skippedCount    = data.items.filter(i => i.status === "skipped").length

    // 배치 내 중복 — registered 가 아닌 items 사이 매칭 (skipped 포함, registered 만 제외).
    //   한 행 등록되면 그 행 그룹에서 빠짐 → 다른 행의 중복 chip 자동 해제.
    //   사용자가 두 번째 등록 시도 시 Registrar 가 DB 중복 재검사로 block (UploadItemRegistrar).
    //   조건 1. file_hash 같음 (정확히 같은 PDF)
    //   조건 2. 정규화(name) + 정규화(manufacturer) 같음 (같은 제품, 다른 PDF)
    const reviewable = data.items.filter(i => i.status !== "registered")
    const normalize = (s) => (s || "").toLowerCase().replace(/\s+/g, "").replace(/[^a-z0-9가-힣]/g, "")
    const hashGroups = {}
    const nameGroups = {}
    reviewable.forEach(i => {
      if (i.file_hash) (hashGroups[i.file_hash] ||= []).push(i.position)
      const key = normalize(i.product_name) + "||" + normalize(i.manufacturer)
      if (key !== "||") (nameGroups[key] ||= []).push(i.position)
    })
    this._dupHashGroups = hashGroups  // hash → [position, ...]
    this._dupNameGroups = nameGroups
    this._dupNormalize  = normalize

    // 요약
    const todoCount = visibleItems.length - skippedCount
    const summaryParts = [`처리 대기 ${todoCount}건`]
    if (skippedCount > 0)    summaryParts.push(`보류 ${skippedCount}건`)
    if (registeredCount > 0) summaryParts.push(`등록 ${registeredCount}건`)
    this.summaryTarget.textContent = summaryParts.join(" · ")

    // 액션 바 — visibleItems (registered/skipped 제외) 기준.
    //   1) visibleItems 비어있음 — todo 0 → [새 배치 시작]
    //   2) pending > 0           — 처리 중...
    //   3) registerable > 0      — [모두 등록 (N)] 활성
    const allDone = visibleItems.length === 0
    if (allDone) {
      this.actionLabelTarget.textContent = "모든 파일 처리가 끝났습니다"
      this.registerAllBtnTarget.innerHTML = `<i data-lucide="plus" style="width: 14px; height: 14px;"></i> 새로 업로드`
      this.registerAllBtnTarget.dataset.action = "click->msds-batch-progress#newBatch"
      this.registerAllBtnTarget.disabled = false
      this.registerAllBtnTarget.classList.remove("bg-[var(--success-700)]", "hover:bg-[#1B5E20]")
      this.registerAllBtnTarget.classList.add("bg-[var(--primary-700)]", "hover:bg-[var(--primary-900)]")
    } else if (data.pending_count > 0) {
      this.actionLabelTarget.textContent = `처리 중... (${data.pending_count}건 대기) · 등록 가능 ${data.registerable_count}건`
      this.registerAllBtnTarget.disabled = data.registerable_count === 0
    } else {
      // pending 0 + visibleItems > 0 — 모두 추출 완료, 사용자 등록 대기
      this.actionLabelTarget.textContent = `등록 가능한 항목: ${data.registerable_count}건`
      this.registerAllBtnTarget.disabled = data.registerable_count === 0
    }

    // items — registered/skipped 제외
    const tbody = this.tableBodyTarget
    tbody.innerHTML = ""
    visibleItems.forEach((item) => {
      tbody.appendChild(this._rowFor(item))
    })

    if (typeof window.lucide !== "undefined") window.lucide.createIcons()
  }

  _rowFor(item) {
    const meta = this.constructor.STATUS_META[item.status] || { label: item.status, color: "gray-500", icon: "circle" }
    const tr = document.createElement("tr")
    tr.className = "border-b border-[var(--gray-100)] hover:bg-[var(--gray-50)]"

    // #
    tr.appendChild(this._td(`<span class="text-xs text-[var(--gray-500)]">${item.position}</span>`))

    // 파일명
    tr.appendChild(this._td(`<span class="truncate block max-w-[200px]" title="${this._esc(item.filename)}">${this._esc(item.filename)}</span>`))

    // 중복 chip — 같은 그룹 중에서 가장 먼저 추가된 것 (min position) 은 '원본' 으로 두고,
    // 그 이후 추가된 행에만 '중복' 표시. peers = 원본 행 번호.
    // 보류(skipped) 행 자체는 상태 chip 우선 — 중복 chip 안 보임 (보류 라벨이 더 명확).
    let dupReason = null  // "PDF" or "제품"
    let peers = null
    if (item.status !== "skipped" && item.file_hash && this._dupHashGroups?.[item.file_hash]?.length > 1) {
      const group = this._dupHashGroups[item.file_hash]
      const originPos = Math.min(...group)
      if (item.position !== originPos) {
        dupReason = "PDF"
        peers = [originPos]
      }
    } else if (item.status !== "skipped" && item.product_name && item.manufacturer && this._dupNormalize) {
      const key = this._dupNormalize(item.product_name) + "||" + this._dupNormalize(item.manufacturer)
      const group = this._dupNameGroups?.[key]
      if (group?.length > 1) {
        const originPos = Math.min(...group)
        if (item.position !== originPos) {
          dupReason = "제품"
          peers = [originPos]
        }
      }
    }
    let statusHtml
    if (dupReason && peers && peers.length > 0) {
      const peersStr = peers.length <= 2
        ? peers.map(p => `#${p}`).join(", ")
        : `#${peers[0]} 외 ${peers.length - 1}건`
      const title = dupReason === "PDF"
        ? `같은 PDF — ${peers.map(p => `#${p}`).join(", ")}`
        : `같은 제품 (제품명·제조사 일치) — ${peers.map(p => `#${p}`).join(", ")}`
      statusHtml = `<span class="inline-flex items-center gap-1 text-[var(--warning-500)]" title="${title}">
        <i data-lucide="alert-triangle" style="width: 12px; height: 12px;"></i>
        <span class="text-xs">중복 ↔ ${peersStr}</span>
      </span>`
    } else {
      statusHtml = `<span class="inline-flex items-center gap-1 text-[var(--${meta.color})]">
        <i data-lucide="${meta.icon}" style="width: 12px; height: 12px;"></i>
        <span class="text-xs">${meta.label}</span>
      </span>`
    }
    tr.appendChild(this._td(statusHtml))

    // 제품 / 제조사
    const name = item.product_name || ""
    const mfg  = item.manufacturer || ""
    const display = (name || mfg)
      ? `<span class="text-xs">${this._esc(name)}${mfg ? ` <span class="text-[var(--gray-400)]">/ ${this._esc(mfg)}</span>` : ""}</span>`
      : (item.error_message
          ? `<span class="text-xs text-[var(--danger-700)]" title="${this._esc(item.error_message)}">${this._esc(item.error_message).substring(0, 40)}</span>`
          : `<span class="text-xs text-[var(--gray-400)]">-</span>`)
    tr.appendChild(this._td(display))

    // 액션: [검토] + [삭제]. skipped(보류) 도 다시 검토·등록 가능.
    const actionTd = document.createElement("td")
    actionTd.className = "px-3 py-2 text-right whitespace-nowrap"
    if (item.review_path) {
      const a = document.createElement("a")
      a.href = item.review_path
      a.className = "inline-flex items-center gap-1 text-xs text-[var(--primary-700)] hover:underline mr-2"
      a.innerHTML = `검토 <i data-lucide="chevron-right" style="width: 12px; height: 12px;"></i>`
      actionTd.appendChild(a)
    }
    if (item.delete_path) {
      const del = document.createElement("button")
      del.type = "button"
      del.dataset.action = "click->msds-batch-progress#deleteItem"
      del.dataset.itemId = item.id
      del.dataset.url = item.delete_path
      del.dataset.filename = item.filename
      del.className = "inline-flex items-center justify-center w-6 h-6 rounded text-[var(--gray-400)] hover:text-[var(--danger-700)] hover:bg-[var(--danger-50)] transition-colors"
      del.setAttribute("aria-label", "삭제")
      del.innerHTML = `<i data-lucide="trash-2" style="width: 14px; height: 14px;"></i>`
      actionTd.appendChild(del)
    }
    tr.appendChild(actionTd)

    return tr
  }

  // [삭제] — confirm 후 DELETE 요청 → 즉시 폴링 갱신.
  async deleteItem(event) {
    const btn = event.currentTarget
    const url = btn.dataset.url
    const filename = btn.dataset.filename || "이 항목"
    if (!confirm(`${filename} 을(를) 삭제하시겠습니까?`)) return

    const csrf = document.querySelector('meta[name="csrf-token"]')?.content
    try {
      const res = await fetch(url, {
        method: "DELETE",
        headers: { "X-CSRF-Token": csrf, "Accept": "application/json" },
        credentials: "same-origin"
      })
      const json = await res.json()
      if (json.success) {
        this._poll() // 즉시 갱신
      } else {
        alert(json.error?.message || "삭제 실패")
      }
    } catch (err) {
      alert("네트워크 오류: " + err.message)
    }
  }

  _td(html) {
    const td = document.createElement("td")
    td.className = "px-3 py-2 align-middle"
    td.innerHTML = html
    return td
  }

  _esc(s) {
    const div = document.createElement("div")
    div.appendChild(document.createTextNode(String(s || "")))
    return div.innerHTML
  }

  // 상단 [모두 등록] — extracted 행 일괄 등록.
  async registerAll() {
    if (!this.batchId) return
    this.registerAllBtnTarget.disabled = true
    const url = this.registerAllUrlTemplateValue.replace("BATCH_ID", this.batchId)
    const csrf = document.querySelector('meta[name="csrf-token"]')?.content
    // [모두 등록] 시점의 현재 선택 부서를 함께 전송 — 업로드 이후 부서를 바꿔도
    // 최종 선택 부서로 등록되도록 (개별 아이템에 지정된 부서는 서버에서 유지).
    const deptCode = (document.querySelector('input[name="department_code"]') || {}).value || ""
    const formData = new FormData()
    if (deptCode) formData.append("department_code", deptCode)
    try {
      const res = await fetch(url, {
        method: "POST",
        headers: { "X-CSRF-Token": csrf, "Accept": "application/json" },
        credentials: "same-origin",
        body: formData
      })
      const json = await res.json()
      if (json.success) {
        const { registered, failed, failures } = json.data
        let msg = `${registered}건 등록 완료`
        if (failed > 0) {
          const sample = failures.slice(0, 3).map(f => `#${f.position}: ${f.message}`).join("\n")
          msg += `, ${failed}건 실패\n\n${sample}${failures.length > 3 ? "\n…" : ""}`
        }
        alert(msg)
        this._poll() // 즉시 갱신
      } else {
        alert(json.error?.message || "등록 실패")
        this.registerAllBtnTarget.disabled = false
      }
    } catch (err) {
      alert("네트워크 오류: " + err.message)
      this.registerAllBtnTarget.disabled = false
    }
  }

  // [새 배치 시작] — 우측 패인 초기화, 폴링 중단.
  newBatch() {
    this._stopPolling()
    this.batchId = null
    this._renderEmpty()
    // 좌측 controller 의 상태도 리셋 — 같은 페이지 안에서 다음 배치 진입을 위해 reload.
    window.location.reload()
  }
}
